import { isPlatformBrowser } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, inject, PLATFORM_ID, OnInit, OnDestroy } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom, Subscription } from 'rxjs';
import { ConceptInvoicingTypeEnums } from 'src/app/enum/concept-invoicing-enums';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { ConceptInvoicingImportModel } from 'src/app/models/imports/concept-invoicing-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-import-concept-invoicing',
  templateUrl: './import-concept-invoicing.component.html',
  styleUrl: './import-concept-invoicing.component.scss',
  standalone: false
})
export class ImportConceptInvoicingComponent implements OnInit, OnDestroy {

  private readonly config = inject(DynamicDialogConfig);
  private readonly conceptInvoicingService = inject(ConceptInvoicingService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly ref = inject(DynamicDialogRef, { optional: true });
  private readonly platformId = inject(PLATFORM_ID);

  conceptInvoicingAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>();
  conceptInvoicingImport: boolean = false;
  eventFile: any;
  errorConceptInvoicing: number = 0;
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = '';
  visibleTxt: boolean = false;
  conceptInvoicingType: number = 1;
  subscription: Subscription;

  ngOnInit() {
    if (this.config.data?.inputs$) {
      this.subscription = this.config.data.inputs$.subscribe(({ conceptInvoicingType }: any) => {
        if (conceptInvoicingType) {
          this.conceptInvoicingType = conceptInvoicingType;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  attachFileImport() {
    //validación para ejecutar el código cuando la platafoma de uso sea un navegador
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('fileImport')?.click();
    }
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      if (isPlatformBrowser(this.platformId)) {
        this.eventFile.target.value = '';
      }
    }
  }

  close() {
    const data: ModalObjectType = {};
    if (this.conceptInvoicingImport) {
      data.params = { reload: true };
      this.ref?.close(data);
    } else {
      data.params = { reload: false };
      this.ref?.close(data);
    }
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files && files.length !== 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  downloadGuideImport() {
    if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
      this.conceptInvoicingService.downloadPurchaseGuideImport().subscribe(data => {
        if (data.type === HttpEventType.Response) {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = 'GuiaConceptosCompra.docx';
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
        }
      });
    } else {
      this.conceptInvoicingService.downloadGuideImport().subscribe(data => {
        if (data.type === HttpEventType.Response) {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = 'GuiaConceptosFacturacion.docx';
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
        }
      });
    }
  }

  downloadErrorsTxt() {
    if (!this.conceptInvoicingAlerts || this.conceptInvoicingAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar'
      );
      return;
    }

    this.conceptInvoicingService.exportImportErrors(this.conceptInvoicingAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];
        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionConceptosFacturacion_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  importConceptInvoicing() {
    this.conceptInvoicingAlerts = [];
    this.errorConceptInvoicing = 0;
    this.visibleTxt = false;
    if (isPlatformBrowser(this.platformId)) {
      let isOk: boolean = true;
      const input = document.getElementById("fileImport") as any;
      const rowsImports = new Array<ConceptInvoicingImportModel>();
      let row: ConceptInvoicingImportModel;
      let counter: number = 1, code: number, alterCode: string | null, alterCode2: string | null,
        name: string, cxcConcept: string, conceptInvoicingPriceOfId: number, affectsInventory: boolean,
        promptPayment: boolean;

      if (input.files[0]) {
        readXlsxFile(input.files[0]).then(async (rows) => {
          this.conceptInvoicingAlerts = [];
          this.visibleTxt = false;
          if (rows.length <= 1) {
            this.toastHandlerService.showToast(
              'warning',
              `El archivo ingresado no tiene registros para importar.`
            );
            return;
          }
          rows.splice(0, 1);
          rows.forEach(element => {
            counter = counter + 1;
            let rowHasError = false;
            //A. Código
            code = parseFloat(element[0] as string);
            if (isNaN(code) || code <= 0) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna A. El código del concepto de facturación no es válido.`;
              this.conceptInvoicingAlerts.push(alert);
              this.errorConceptInvoicing = 1;
              rowHasError = true;
            }

            //B. Codigo Alterno
            alterCode = element[1] as string;
            if (alterCode != null && alterCode.length > 10) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. El código alterno supera el tamaño máximo de caracteres (10)`
              this.conceptInvoicingAlerts.push(alert);
              rowHasError = true;
            }

            //C. Codigo Alterno
            alterCode2 = element[2] as string;
            if (alterCode2 != null && alterCode2.length > 10) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. El código alterno supera el tamaño máximo de caracteres (10)`
              this.conceptInvoicingAlerts.push(alert);
              rowHasError = true;
            }

            //D. Nombre:
            name = element[3] as string;
            if (name == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna D. El nombre no es válido.`;
              this.conceptInvoicingAlerts.push(alert);
              rowHasError = true;
            } else if (name.length > 300) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna D. El nombre supera el tamaño máximo de caracteres (300)`;
              this.conceptInvoicingAlerts.push(alert);
              rowHasError = true;
            }
            if (this.conceptInvoicingType == ConceptInvoicingTypeEnums.venta) {
              //E. Concepto CxC
              cxcConcept = element[4] as string;
              if (cxcConcept != null && cxcConcept.length > 10) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna E. El Concepto CxC supera el tamaño máximo de caracteres (10)`;
                this.conceptInvoicingAlerts.push(alert);
                rowHasError = true;
              }

              //F. Concept Invoicing Price Of
              conceptInvoicingPriceOfId = parseFloat(element[5] as string);
              if (isNaN(conceptInvoicingPriceOfId)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna F. El precio del concepto de facturación no es válido.`;
                this.conceptInvoicingAlerts.push(alert);
                rowHasError = true;
              } else if (conceptInvoicingPriceOfId < 1 || conceptInvoicingPriceOfId > 4) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna F. El precio del concepto de facturación no deben ser valores como:\n \t 1 - Ningúno \n \t 2 - Reposición \n \t 3 - Venta \n \t 4 - Alquiler.`;
                this.conceptInvoicingAlerts.push(alert);
                rowHasError = true;
              }

              //G. Affecta Inventario
              if (element[6] as string != "S" && element[6] as string != "N") {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna F. El campo afecta inventario debe ser valores como: S / N.`;
                this.conceptInvoicingAlerts.push(alert);
                rowHasError = true;
              }
              affectsInventory = element[6] as string === "S";

              //H. Pronto Pago
              if (element[7] as string != "S" && element[7] as string != "N") {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna H. El campo pronto pago debe ser valores como: S / N.`;
                this.conceptInvoicingAlerts.push(alert);
                rowHasError = true;
              }

              promptPayment = element[7] as string === "S";
            }
            if (!rowHasError) {
              row = new ConceptInvoicingImportModel(counter, code, alterCode, alterCode2, name,
                cxcConcept, conceptInvoicingPriceOfId, affectsInventory, promptPayment, this.conceptInvoicingType);
              rowsImports.push(row);
            } else {
              isOk = false;
            }
          });
          if (isOk && rowsImports.length > 0) {
            try {
              const response = await firstValueFrom(this.conceptInvoicingService.importConceptInvoicing(rowsImports));
              this.loadPreInvoicingModel = response;
              this.conceptInvoicingAlerts = response?.alerts ?? [];
              this.errorConceptInvoicing = response?.preInvoicingStateId;
              if (this.errorConceptInvoicing === 1) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con errores, puede exportarlos si lo desea.',
                  'ERROR'
                );
              } else if (this.conceptInvoicingAlerts.length > 0) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con alertas, puede exportarlas si lo desea'
                );
              } else {
                this.visibleTxt = false;
                this.conceptInvoicingImport = true;
                this.toastHandlerService.showToast(
                  'success',
                  `La importación terminó exitosamente.`
                );
                this.close();
              }
              this.clearInputFile();
            } catch (error) {
              this.nameFile = "Ningún archivo seleccionado."
              console.error('Error al importar conceptos de facturación', error);
            }
          } else {
            this.errorConceptInvoicing = 1;
            this.visibleTxt = true;
            this.toastHandlerService.showToast(
              'error',
              'La importación terminó con errores, puede exportarlos si lo desea'
            );
            this.clearInputFile();
            return;
          }
        });
      } else {
        this.toastHandlerService.showToast('error', 'No ha seleccionado ningún archivo.');
      }
    }
  }
}
